import { useCallback, type ReactNode, type ReactElement, type MouseEvent } from 'react';
import cn from 'classnames';
import IconClose from '@creditflowresearch/uilib/Icon/IconClose';
import Grid from '@creditflowresearch/uilib/Grid';
import GridItem from '@creditflowresearch/uilib/GridItem';
import Flex from '@creditflowresearch/uilib/Flex';
import Button from '@creditflowresearch/uilib/Button';
import PortalWrp, { PortalId } from '@framework/ui/Templates/PortalWrp';
import { useCloseConfirmation } from './useCloseConfirmation';
import { CloseConfirmation } from './CloseConfirmation';

import styles from './sidebarSlider.module.scss';

interface ISidebarSliderProps {
  isOpen: boolean,
  onClose: () => void,
  header?: ReactNode | null,
  children?: ReactNode,
  footer?: ReactNode,
  confirmOnClose?: boolean,
  confirmationTitle?: string,
  confirmationMessage?: string,
  confirmLabel?: string,
  cancelLabel?: string,
  width?: number,
  dataTest?: string,
  // TODO: remove after customization panel full merge
  headerWithShadow?: boolean,
}

const SidebarSlider = ({
  isOpen,
  onClose,
  header,
  children,
  footer,
  confirmOnClose = false,
  confirmationTitle,
  confirmationMessage,
  confirmLabel,
  cancelLabel,
  width = 700,
  dataTest = 'sidebarSlider',
  headerWithShadow = false,
}: ISidebarSliderProps): ReactElement => {
  const {
    confirmPosition, confirmRef, requestClose, confirmClose, cancelClose,
  } = useCloseConfirmation(onClose, confirmOnClose);

  const handleClose = useCallback((e: MouseEvent): void => requestClose(e), [requestClose]);

  return (
    <PortalWrp portalId={ PortalId.PORTAL_OVERLAY_ID }>
      <>
        <div className={ cn(styles.overlay, { [styles.isOpen]: isOpen }) } onClick={ handleClose } />
        <Grid
          templateRows={ header !== null ? 'auto 1fr auto' : '1fr auto' }
          templateColumns="1fr"
          className={ cn(styles.panel, { [styles.isOpen]: isOpen }) }
          style={ { '--panel-width': `${ width }px` } as React.CSSProperties }
          dataTest={ dataTest }
        >
          { header !== null && (
            <GridItem className={ cn(styles.header, { [styles.headerWithShadow]: headerWithShadow }) } dataTest="sidebarSliderHeader">
              <Flex align="center" justify="space-between">
                { header }
              </Flex>
            </GridItem>
          ) }
          { isOpen && (
            <Button
              className={ styles.closeButton }
              variant="text"
              icon={ <IconClose /> }
              ariaLabel="Close"
              dataTest="closeFiltersButton"
              onClick={ handleClose }
            />
          ) }
          <GridItem className={ styles.body } dataTest="sidebarSliderBody">
            { children }
          </GridItem>
          { footer && (
            <GridItem className={ styles.footer } dataTest="sidebarSliderFooter">
              { footer }
            </GridItem>
          ) }
        </Grid>
        { confirmPosition && (
          <CloseConfirmation
            position={ confirmPosition }
            title={ confirmationTitle }
            message={ confirmationMessage }
            confirmLabel={ confirmLabel }
            cancelLabel={ cancelLabel }
            confirmRef={ confirmRef }
            onConfirm={ confirmClose }
            onCancel={ cancelClose }
          />
        ) }
      </>
    </PortalWrp>
  );
};

export { SidebarSlider };
export type { ISidebarSliderProps };
